import React from 'react';

/**
 * TekMark — the stylised "T" glyph (modelled after tekflix.co), paired with
 * the rest of the wordmark. The glyph IS the leading "T", so the wordmark
 * starts at "ek..." (Tekflix shows "ekFLIX" beside the glyph; we show "ekHUB").
 *
 * Theme: colours come from CSS custom properties defined in
 * src/styles/theme.css. Change them there and the mark re-skins instantly.
 *
 * Variants:
 *   <TekMark />                            → glyph + "ekHUB"
 *   <TekMark word="ekStudio" />            → glyph + custom tail
 *   <TekMark wordmark={false} />           → glyph only
 *   <TekMark size={32} />                  → control mark height
 *   <TekMark boldUntil={2} />              → bold the first N chars of word
 */
export default function TekMark({
  size = 28,
  word = 'ekHUB',
  boldUntil = 2,
  wordmark = true,
  className = '',
}) {
  const id = React.useId();
  const head = word.slice(0, boldUntil);
  const tail = word.slice(boldUntil);
  return (
    <span
      className={`tekmark ${className}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: size * 0.05, lineHeight: 1 }}
    >
      <svg
        width={size * (44 / 48)}
        height={size}
        viewBox="0 0 44 48"
        fill="none"
        aria-hidden="true"
        style={{ flex: 'none' }}
      >
        <defs>
          <linearGradient id={`tek-mark-${id}`} x1="0" y1="0" x2="40" y2="48" gradientUnits="userSpaceOnUse">
            <stop offset="0"    stopColor="var(--tek-mark-1, #14301F)" />
            <stop offset="0.35" stopColor="var(--tek-mark-2, #1F4A30)" />
            <stop offset="0.6"  stopColor="var(--tek-mark-3, #2A5A40)" />
            <stop offset="0.8"  stopColor="var(--tek-mark-4, #3D7558)" />
            <stop offset="1"    stopColor="var(--tek-mark-5, #4F8567)" />
          </linearGradient>
        </defs>
        {/* Horizontal crossbar — full width, rounded ends */}
        <rect
          x="0" y="0"
          width="44" height="12"
          rx="6"
          fill={`url(#tek-mark-${id})`}
        />
        {/* Vertical stem — centred, rounded bottom */}
        <rect
          x="16" y="0"
          width="12" height="48"
          rx="6"
          fill={`url(#tek-mark-${id})`}
        />
      </svg>

      {wordmark && (
        <span
          className="tekmark-word"
          style={{
            fontFamily: 'Onest, system-ui, sans-serif',
            fontSize: size * 0.62,
            letterSpacing: '-0.02em',
            lineHeight: 1,
          }}
        >
          <span style={{ fontWeight: 700, color: 'var(--tek-word-bold, currentColor)' }}>{head}</span>
          <span style={{ fontWeight: 500, color: 'var(--tek-word-light, currentColor)' }}>{tail}</span>
        </span>
      )}
    </span>
  );
}

/**
 * Small "Powered by Tekflix" credit chip.
 * Use on the landing page footer, sign-in/up footer, or anywhere
 * a discreet attribution is appropriate.
 */
export function PoweredByTekflix({ size = 14, muted = true, className = '' }) {
  return (
    <a
      href="https://tekflix.co"
      target="_blank"
      rel="noopener"
      className={`powered-by-tekflix ${className}`}
      aria-label="Powered by Tekflix — opens in a new tab"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        textDecoration: 'none',
        fontSize: size * 0.86,
        letterSpacing: '0.01em',
        color: muted ? 'var(--ink-mute)' : 'var(--ink-2)',
        fontFamily: 'Onest, system-ui, sans-serif',
        transition: 'color .15s, opacity .15s',
        opacity: muted ? 0.85 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      <span>Powered by</span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontWeight: 600,
          color: 'var(--tek-credit-color, var(--ink))',
        }}
      >
        <svg width="14" height="13" viewBox="0 0 85 70" fill="none" aria-hidden style={{ display: 'inline-block' }}>
          <path d="M64.87 0L34.79 0.11C31.85 0.11 29.04 1.27 26.97 3.34C24.91 5.42 23.75 8.23 23.75 11.16L23.84 57.91C23.84 64.42 30.93 67.69 35.71 65.34C37.6 64.42 38.99 62.79 39.59 60.81C40.19 58.83 40.06 56.69 39.23 54.81L39.16 28.41C39.16 26.94 39.74 25.53 40.78 24.48C41.82 23.44 43.22 22.85 44.69 22.84H56.5C58 22.83 59.48 22.52 60.86 21.94C62.24 21.36 63.49 20.51 64.54 19.45C65.6 18.38 66.43 17.12 66.99 15.74C67.56 14.35 67.84 12.86 67.83 11.36C67.83 9.83 67.52 8.32 66.94 6.91C66.36 5.5 65.5 4.23 64.42 3.16C63.34 2.09 62.06 1.25 60.65 0.69C59.24 0.13 57.74 -0.13 56.23 -0.04L64.87 0Z" fill="currentColor" />
          <circle cx="11.42" cy="11.54" r="11.42" fill="currentColor" />
        </svg>
        Tekflix
      </span>
    </a>
  );
}
